/** Rendered gold/glass rewards, never a scoring authority or input layer. One
 * medal and twelve small sprites at most; decode, motion preference, visibility
 * and cancellation are checked before a late image can create any decoration. */
export function createArcadePrizes(ctx, playfield) {
  let loading = null,
    generation = 0,
    disposed = false,
    expiry;
  const doc = playfield.ownerDocument ?? globalThis.document;
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const suspend = () => {
    if (doc?.hidden || media.matches) cancel();
  };
  const nodes = new Set(),
    animations = new Set();
  function prepare() {
    if (disposed || loading || typeof Image === 'undefined') return loading;
    const image = new Image();
    image.src = new URL('../assets/game-arcade-prizes-v1.webp', import.meta.url).href;
    loading =
      typeof image.decode === 'function'
        ? image.decode().then(
            () => true,
            () => false,
          )
        : Promise.resolve(false);
    return loading;
  }
  function cancel() {
    generation++;
    clearTimeout(expiry);
    doc?.removeEventListener?.('visibilitychange', suspend);
    media.removeEventListener?.('change', suspend);
    for (const animation of animations) animation.cancel();
    animations.clear();
    for (const node of nodes) node.remove();
    nodes.clear();
  }
  function sprite(kind, x, y) {
    const node = ctx.e('span', 'arcade-prize-sprite');
    node.dataset.prize = String(kind);
    node.setAttribute('aria-hidden', 'true');
    node.style.left = `${x}%`;
    node.style.top = `${y}%`;
    playfield.append(node);
    nodes.add(node);
    return node;
  }
  function animate(node, frames, options) {
    if (typeof node.animate !== 'function') return;
    const animation = node.animate(frames, { fill: 'both', ...options });
    animations.add(animation);
    void animation.finished.then(
      () => {
        animations.delete(animation);
        node.remove();
        nodes.delete(node);
      },
      () => {},
    );
  }
  function play({ finale = false, x = 50, y = 40 } = {}) {
    cancel();
    const ready = prepare();
    if (!ready || disposed) return;
    const epoch = generation;
    doc?.addEventListener?.('visibilitychange', suspend);
    media.addEventListener?.('change', suspend);
    expiry = setTimeout(cancel, 1100);
    void ready.then((decoded) => {
      if (!decoded || disposed || epoch !== generation || doc?.hidden || !playfield.isConnected)
        return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (finale) {
        const medal = sprite(0, x, Math.max(16, y - 14));
        if (!reduced)
          animate(
            medal,
            [
              { transform: 'translate(-50%, -50%) scale(.3) rotate(-12deg)', opacity: 0 },
              {
                transform: 'translate(-50%, -50%) scale(1.12) rotate(3deg)',
                opacity: 1,
                offset: 0.25,
              },
              { transform: 'translate(-50%, -50%) scale(1)', opacity: 1, offset: 0.7 },
              { transform: 'translate(-50%, -65%) scale(.95)', opacity: 0 },
            ],
            { duration: 1050, easing: 'cubic-bezier(.16,.8,.25,1)' },
          );
      }
      if (!reduced)
        for (let i = 0; i < (finale ? 12 : 6); i++) {
          const angle = (i / (finale ? 12 : 6)) * Math.PI * 2;
          const radius = finale ? 94 : 60;
          const node = sprite(1 + (i % 3), x, y);
          const dx = Math.cos(angle) * radius,
            dy = Math.sin(angle) * radius;
          animate(
            node,
            [
              { transform: 'translate(-50%, -50%) scale(.25)', opacity: 0 },
              {
                transform: `translate(calc(-50% + ${dx * 0.45}px), calc(-50% + ${dy * 0.45 - 15}px)) scale(1) rotate(${i * 19}deg)`,
                opacity: 1,
                offset: 0.24,
              },
              {
                transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + 35}px)) scale(.5) rotate(${i * 37}deg)`,
                opacity: 0,
              },
            ],
            {
              duration: 850 + (i % 3) * 60,
              delay: (i % 3) * 35,
              easing: 'cubic-bezier(.15,.65,.35,1)',
            },
          );
        }
    });
  }
  return {
    prepare,
    play,
    cancel,
    dispose() {
      disposed = true;
      cancel();
      loading = null;
    },
  };
}
